import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Copy, Check, Ticket, Trash2 } from 'lucide-react';
import { queryKeys, type InviteSummary } from '@vtak/shared';
import { workspaceClient, type MintedInvite } from '@/services/workspace-client';
import { QrImage } from '@/components/common/qr-image';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

/** Selectable invite lifetimes. */
const TTL_OPTIONS: ReadonlyArray<{ label: string; ms: number }> = [
  { label: '1 hour', ms: 60 * 60 * 1000 },
  { label: '24 hours', ms: 24 * 60 * 60 * 1000 },
  { label: '7 days', ms: 7 * 24 * 60 * 60 * 1000 },
];

/** Mint join codes and manage active invites for a unit. */
export function InviteMint({ unitId }: { unitId: string }): React.JSX.Element {
  const [ttlMs, setTtlMs] = useState(TTL_OPTIONS[1]?.ms ?? 0);
  const [maxUses, setMaxUses] = useState(1);
  const client = useQueryClient();

  const mint = useMutation({
    mutationFn: () => workspaceClient.mintInvite(unitId, ttlMs, maxUses),
    onSuccess: () => { void client.invalidateQueries({ queryKey: queryKeys.workspace.invites(unitId) }); },
  });

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-3">
      <MintForm
        ttlMs={ttlMs}
        maxUses={maxUses}
        onTtl={setTtlMs}
        onMaxUses={setMaxUses}
        pending={mint.isPending}
        onMint={() => mint.mutate()}
      />
      {mint.data && <MintedCode invite={mint.data} />}
      <ActiveInvites unitId={unitId} />
    </div>
  );
}

/** TTL + max-uses selector and the generate button. */
function MintForm({
  ttlMs,
  maxUses,
  onTtl,
  onMaxUses,
  pending,
  onMint,
}: {
  ttlMs: number;
  maxUses: number;
  onTtl: (ms: number) => void;
  onMaxUses: (n: number) => void;
  pending: boolean;
  onMint: () => void;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2 rounded-md border border-echo-border bg-white/[0.02] p-3">
      <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Expires in</label>
      <div className="flex gap-1.5">
        {TTL_OPTIONS.map((opt) => (
          <button
            key={opt.ms}
            type="button"
            onClick={() => onTtl(opt.ms)}
            className={cn(
              'flex-1 rounded-md border px-2 py-1.5 text-xs transition-colors',
              ttlMs === opt.ms
                ? 'border-echo-yellow/60 bg-echo-yellow/15 text-echo-yellow'
                : 'border-echo-border text-muted-foreground hover:bg-white/[0.06]',
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <label className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Max uses</label>
      <Input
        type="number"
        min={1}
        value={maxUses}
        onChange={(e) => onMaxUses(Math.max(1, Number(e.target.value) || 1))}
        className="h-8"
      />
      <Button className="mt-1 gap-1.5" disabled={pending} onClick={onMint}>
        <Ticket className="h-4 w-4" /> {pending ? 'Generating…' : 'Generate join code'}
      </Button>
    </div>
  );
}

/** The freshly minted code with copy + deep-link actions. */
function MintedCode({ invite }: { invite: MintedInvite }): React.JSX.Element {
  const deepLink = `${window.location.origin}/?join=${invite.code}`;
  return (
    <div className="flex flex-col gap-2 rounded-md border border-echo-yellow/40 bg-echo-yellow/[0.06] p-3">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Join code — share once, shown only now
      </p>
      <div className="flex items-center gap-2">
        <code className="flex-1 break-all rounded bg-black/30 px-2 py-2 text-center font-mono text-sm tracking-wider text-echo-yellow">
          {invite.code}
        </code>
        <CopyButton value={invite.code} label="code" />
      </div>
      <div className="flex items-center gap-2">
        <span className="flex-1 truncate text-[11px] text-muted-foreground">{deepLink}</span>
        <CopyButton value={deepLink} label="link" />
      </div>
      <div className="mt-1 flex flex-col items-center gap-1.5">
        <QrImage value={deepLink} size={160} className="rounded bg-white p-1.5" />
        <p className="text-[10px] text-muted-foreground">Scan to join — member or guest</p>
      </div>
    </div>
  );
}

/** A copy-to-clipboard button that confirms with a check for ~1.5s. */
function CopyButton({ value, label }: { value: string; label: string }): React.JSX.Element {
  const [copied, setCopied] = useState(false);

  function copy(): void {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <Button size="sm" variant="outline" className="h-8 shrink-0 gap-1 px-2 text-xs" onClick={copy} title={`Copy ${label}`}>
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : 'Copy'}
    </Button>
  );
}

/** List of active invites with revoke. */
function ActiveInvites({ unitId }: { unitId: string }): React.JSX.Element {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: queryKeys.workspace.invites(unitId),
    queryFn: () => workspaceClient.getInvites(unitId),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => workspaceClient.revokeInvite(unitId, id),
    onSuccess: () => { void client.invalidateQueries({ queryKey: queryKeys.workspace.invites(unitId) }); },
  });

  const invites = (query.data ?? []).filter((inv) => !inv.revoked);
  if (invites.length === 0) {
    return <p className="text-center text-xs text-muted-foreground">No active invites.</p>;
  }

  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Active invites</p>
      {invites.map((invite) => (
        <InviteRow key={invite.id} invite={invite} onRevoke={() => revoke.mutate(invite.id)} />
      ))}
    </div>
  );
}

/** A single active invite row. */
function InviteRow({ invite, onRevoke }: { invite: InviteSummary; onRevoke: () => void }): React.JSX.Element {
  return (
    <div className="flex items-center justify-between gap-2 rounded-md bg-white/[0.04] px-3 py-2 text-xs">
      <div className="min-w-0">
        <p className="text-foreground">{invite.useCount}/{invite.maxUses} uses</p>
        <p className="text-[10px] text-muted-foreground">expires {formatExpiry(invite.expiresAt)}</p>
      </div>
      <button
        type="button"
        title="Revoke invite"
        onClick={onRevoke}
        className="rounded p-1 text-muted-foreground transition-colors hover:text-red-400"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/** Format an ISO expiry as a short local date-time. */
function formatExpiry(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}
